import React, {Fragment, useState, useContext, useEffect} from 'react';
import {OpenApiContext} from '../openapi-context';
import {CombinedSchemasView} from './combined-schemas';
import {SchemaView} from './schema-view';
import {OpenAPIRef} from './openapi-ref';
import {Typography, Tag} from 'antd';
import {useRouteMatch} from 'react-router-dom';
import {get, cloneDeep} from 'lodash';

// Inline panel for refs we know about but couldn't fetch (target file
// missing, backend resolveRef returned null, etc.). Without this, the
// editor previously rendered an empty area for unresolved refs, which
// gave the wrong impression that the spec body was empty when in fact
// the spec did declare content the editor just couldn't reach.
function UnresolvedRefPanel({refLink, description, summary}) {
    return (
        <div className="unresolvedRefPanel">
            <div className="unresolvedRefPanel__head">
                <Tag color="red">Unresolved</Tag>
                <Typography.Text strong>External reference</Typography.Text>
                <OpenAPIRef link={refLink} unresolved />
            </div>
            {summary && <Typography.Paragraph strong>{summary}</Typography.Paragraph>}
            {description && <Typography.Paragraph>{description}</Typography.Paragraph>}
            <Typography.Text type="secondary">
                The target could not be loaded. The referenced file may be missing, the path may be wrong,
                or the backend may not have access to it. The reference is preserved in the spec.
            </Typography.Text>
        </div>
    );
}

export function MasterSchemaView({schema, editMode, onChange, type, contextPath, offset = 0}) {
    const [resolvedSchema, setResolvedSchema] = useState();
    const [resolvedRef, setResolvedRef] = useState();
    const [unresolvedRef, setUnresolvedRef] = useState(null);
    const [isArray, setIsArray] = useState(false);
    const openapiContext = useContext(OpenApiContext);
    const {params} = useRouteMatch('/:env/standards/:category/:key/:version/:view/:element');

    useEffect(() => {
        async function resolveSchema() {
            if (schema) {
                let targetSchema = cloneDeep(schema);

                type = type || targetSchema.type;

                if (type === 'array') {
                    if (Object.keys(targetSchema.properties || {}).length === 0) {
                        const _$ref = get(targetSchema, 'items.$ref');
                        if (_$ref) {
                            const looked = await openapiContext.state.lookup(_$ref, params);
                            if (looked) {
                                targetSchema = looked;
                                setIsArray(true);
                                setResolvedRef(_$ref);
                                setUnresolvedRef(null);
                            } else {
                                setIsArray(true);
                                setResolvedRef(null);
                                setUnresolvedRef({
                                    ref: _$ref,
                                    description: targetSchema.description,
                                    summary: targetSchema.summary,
                                });
                            }
                        } else {
                            setResolvedRef(null);
                            setUnresolvedRef(null);
                            setIsArray(true);
                        }
                    }
                } else if (targetSchema.$ref) {
                    const _$ref = targetSchema.$ref;
                    const looked = await openapiContext.state.lookup(targetSchema.$ref, params);
                    if (looked) {
                        targetSchema = looked;
                        setResolvedRef(_$ref);
                        setUnresolvedRef(null);
                    } else {
                        setResolvedRef(null);
                        setUnresolvedRef({
                            ref: _$ref,
                            description: targetSchema.description,
                            summary: targetSchema.summary,
                        });
                    }
                    setIsArray(false);
                } else {
                    setResolvedRef(null);
                    setUnresolvedRef(null);
                    setIsArray(false);
                }
                setResolvedSchema(targetSchema);
            }
        }
        resolveSchema();
    }, [schema]);

    if (unresolvedRef) {
        return <UnresolvedRefPanel refLink={unresolvedRef.ref} description={unresolvedRef.description} summary={unresolvedRef.summary} />;
    }
    return resolvedSchema ? (
        <Fragment>
            <CombinedSchemasView editMode={editMode} schemas={resolvedSchema.allOf} type={resolvedSchema.type} offset={offset} contextPath={contextPath} combineOperator="allOf" onChange={onChange} />
            <CombinedSchemasView editMode={editMode} schemas={resolvedSchema.anyOf} type={resolvedSchema.type} offset={offset} contextPath={contextPath} combineOperator="anyOf" onChange={onChange} />
            <CombinedSchemasView editMode={editMode} schemas={resolvedSchema.oneOf} type={resolvedSchema.type} offset={offset} contextPath={contextPath} combineOperator="oneOf" onChange={onChange} />

            {(resolvedSchema.properties || resolvedSchema.items) && (
                <SchemaView
                    isArray={isArray}
                    resolvedRef={resolvedRef}
                    schema={resolvedSchema}
                    type={resolvedSchema.type}
                    offset={offset}
                    contextPath={contextPath}
                    editMode={editMode}
                    onChange={onChange}
                />
            )}
        </Fragment>
    ) : null;
}
